import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { database } from '../../utils/firebase';

const WinnersReport = () => {
    const { leagueId, matchId, print, header } = useParams();
    const [matchDetails, setMatchDetails] = useState({});
    const [players, setPlayers] = useState([]);
    const [leagueSettings, setLeagueSettings] = useState({});
    const [flights, setFlights] = useState([]);
    const [leagueData, setLeagueData] = useState({});
    const [otherPayouts, setOtherPayouts] = useState([]);
    const [payoutSet, setPayoutSet] = useState({});
    const [totalPayout, setTotalPayout] = useState(0);

    useEffect(() => {
        const leagueRef = ref(database, `leagues/${leagueId}/settings`);
        onValue(leagueRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                setLeagueSettings(data);
            }
        });
    }, [leagueId]);

    useEffect(() => {
        const leagueRef = ref(database, `leagues/${leagueId}`);
        onValue(leagueRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                setLeagueData(data);
                setLeagueSettings(data.settings || {});
                const matchData = data.matches[matchId];
                if (matchData) {
                    setMatchDetails(matchData);
                    const matchDate = matchData.dateTime * 1000;
                    const relevantHandicapSet = Object.values(data.handicaps || {}).find(handicapSet => {
                        return handicapSet.startDate * 1000 <= matchDate && matchDate <= handicapSet.endDate * 1000;
                    });
                    setFlights(Object.values(relevantHandicapSet?.flights || {}));
                    const activePlayers = Object.keys(data.players || {}).map(playerId => ({
                        ...data.players[playerId],
                        handicap: relevantHandicapSet?.playerHandicaps[playerId] || 0,
                        gross: matchData.scores[playerId]?.gross || 0,
                        net: matchData.scores[playerId]?.net || 0,
                        payouts: matchData.scores[playerId]?.payouts || { total: 0, details: [] }
                    }));
                    setPlayers(activePlayers);

                    // Find the relevant payout set based on the match date
                    const relevantPayoutSet = Object.values(data.payouts || {}).find(payoutSet => {
                        return payoutSet.startDate * 1000 <= matchDate && matchDate <= payoutSet.endDate * 1000;
                    });
                    setPayoutSet(relevantPayoutSet || {});
                    setOtherPayouts(matchData.otherPayouts || []);

                    // Calculate total payout
                    const totalPayout = activePlayers.reduce((sum, player) => sum + player.payouts.total, 0);
                    setTotalPayout(totalPayout);
                }
            }
            if (print === 'true') {
                setTimeout(() => {
                    window.print();
                }, 100);
            }
        });
    }, [leagueId, matchId]);

    const formatDate = (timestamp) => {
        const options = { month: 'short', day: 'numeric', year: 'numeric' };
        return new Date(timestamp * 1000).toLocaleDateString(leagueSettings.language, options);
    };

    const formatTime = (timestamp) => {
        const options = { hour: 'numeric', minute: 'numeric', hour12: true, timeZone: leagueSettings.timeZone };
        return new Date(timestamp * 1000).toLocaleTimeString(leagueSettings.language, options);
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
    };

    return (
        <div style={{backgroundColor: '#ffffff'}}>
            <div className={'modern-report report-container'}>
                <div className="modern-report-container">
                    {header === 'true' && (
                        <div className={'modern-report-header'} style={{ padding: '5px 0' }}>
                            <h1 style={{ margin: '5px 0', fontSize: '1.2em' }}>Winners Report</h1>
                            <h3 style={{ margin: '5px 0', fontSize: '1em' }}>{leagueData.name}</h3>
                            <h4 style={{ margin: '5px 0', fontSize: '0.8em' }}>{leagueData.parentGroup}</h4>
                            <h4 style={{ margin: '5px 0', fontSize: '0.8em' }}>{matchDetails.name}</h4>
                            <h4 style={{ margin: '5px 0', fontSize: '0.8em' }}>{matchDetails.dateTime ? `${formatDate(matchDetails.dateTime)} - ${formatTime(matchDetails.dateTime)}` : ''}</h4>
                        </div>
                    )}
                    <div className="tables-container">
                        {flights.map((flight, flightIndex) => (
                            <div key={flightIndex} className="flight-table">
                                <h3 style={{marginLeft: "15px"}}>{flight.name}</h3>
                                <table>
                                    <thead>
                                        <tr>
                                            <th width="40%">Player Name</th>
                                            <th width="20%">Score</th>
                                            <th width="20%">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.values(payoutSet.mainPayouts || {}).map((payoutGroup, groupIndex) => (
                                            <React.Fragment key={groupIndex}>
                                                {Object.values(payoutGroup.payouts || {}).map((payout, payoutIndex) => (
                                                    <React.Fragment key={payoutIndex}>
                                                        <tr>
                                                            <th colSpan="3">{payout.name}</th>
                                                        </tr>
                                                        {players.filter(player => player.handicap >= Number(flight.start) && player.handicap <= Number(flight.end) && player.payouts.details.some(detail => detail.name === payout.name)).map((player, index) => (
                                                            <tr key={index}>
                                                                <td>{player.name}</td>
                                                                <td>{payout.type === 'net' ? player.net : player.gross}</td>
                                                                {player.payouts.details.find(detail => detail.name === payout.name)?.amount === 0 ? (
                                                                    <td></td>
                                                                ) : (
                                                                    <td>{formatAmount(player.payouts.details.find(detail => detail.name === payout.name)?.amount.toFixed(2))}</td>
                                                                )}
                                                            </tr>
                                                        ))}
                                                    </React.Fragment>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
                        {payoutSet.otherPayouts && payoutSet.otherPayouts.map((payout, index) => (
                            <div key={index} className="payouts-container">
                                <h3 style={{marginLeft: "15px"}}>{payout.name}</h3>
                                <table style={{width: "275px"}}>
                                    <thead>
                                    <tr>
                                        <th width={"150px"}>Name</th>
                                        {payout.holeBound && <th width={'50px'}>Hole</th>}
                                        <th width={"75px"}>Amount</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {Object.entries(otherPayouts[index] || {}).map(([key, playerId]) => (
                                        <tr key={key}>
                                            <td>{leagueData.players[playerId]?.name || 'Unknown Player'}</td>
                                            {payout.holeBound && <td>{key}</td>}

                                            {Number(payout.amount) === 0 ? (
                                                <td></td>
                                            ) : (
                                                <td>{formatAmount(payout.amount)}</td>
                                            )}
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        ))}

                        <div className="payouts-container">
                            <h3 style={{marginLeft: "15px"}}>Summary</h3>
                            <table style={{width: "200px"}}>
                                <thead>
                                <tr>
                                    <th width={"150px"}>Total Players</th>
                                    <th width={'100px'}>Total Payout</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <td>{players.filter(player => player.gross > 0).length}</td>
                                    {totalPayout === 0 ? (
                                        <td></td>
                                    ) : (
                                        <td>{formatAmount(totalPayout.toFixed(2))}</td>
                                    )}
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WinnersReport;